import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Loader2 } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { usePersonalConnect } from '../../components/settings/PersonalConnect';
import useOpenWorkspace from '../../hooks/useOpenWorkspace';
import { errorText, setupApi } from '../../services/setupApi';
import type { AppPermissions, Decision, SetupApp, SetupIdea, SetupView } from '../../services/setupApi';
import type { ConnectorRow } from '../../types/aai';
import {
  AppMark, BTN, BTN_OUTLINE, BTN_TEXT, Chip, DecisionControl, FIT_LABEL, Footer, KIND_LABEL,
  PANEL, SetupFrame, StepTitle,
} from './setupKit';

type Props = { view: SetupView; onView: (v: SetupView) => void };

/* The Workspace opens in its own tab; this one lands on the dashboard only
   once the crossing has its address, or the tab would never get it. The
   crossing must start inside the click, before any await, or the browser
   blocks the new tab. */
const useCross = () => {
  const { opening, open } = useOpenWorkspace();
  const cross = async (handoff: () => Promise<string>) => {
    let finished = false;
    await open(localStorage.getItem('client_id'), async () => {
      const path = await handoff();
      await setupApi.finish();
      finished = true;
      return path;
    });
    if (finished) window.location.href = '/dashboard';
  };
  return { opening, cross };
};

const goDashboard = async () => {
  try {
    await setupApi.finish();
  } catch {
    // The dashboard is still the right place to land; a later visit finishes it.
  }
  window.location.href = '/dashboard';
};

const neededApps = (view: SetupView, ids: string[]): { app: SetupApp; ideas: string[] }[] => {
  const chosen = new Set(ids);
  const ideas: SetupIdea[] = (view.setup?.ideas || []).filter((i) => chosen.has(i.id));
  const apps = view.setup?.apps || [];
  return apps
    .map((app) => ({ app, ideas: ideas.filter((i) => i.apps.includes(app.label)).map((i) => i.title) }))
    .filter((row) => row.ideas.length > 0);
};

// ── 03 Permissions ──────────────────────────────────────────────────────────

export const PermissionsStep: React.FC<Props> = ({ view, onView }) => {
  const apps = view.setup?.apps || [];
  const [active, setActive] = useState(apps[0]?.slug || '');
  const [perms, setPerms] = useState<AppPermissions | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    setOpen({});
    setupApi.permissions(active)
      .then(({ data }) => setPerms(data))
      .catch(() => setPerms(null))
      .finally(() => setLoading(false));
  }, [active]);

  const save = async (decisions: Record<string, Decision>) => {
    setSaving(true);
    try {
      const { data } = await setupApi.setPermissions(active, decisions);
      setPerms(data);
      setTouched((t) => new Set(t).add(active));
    } catch (err) {
      toast.error(errorText(err, 'Could not save that permission.'));
    } finally {
      setSaving(false);
    }
  };

  const setGroup = (key: string, decision: Decision) => {
    const group = perms?.groups.find((g) => g.key === key);
    if (!group) return;
    save(Object.fromEntries(group.actions.map((a) => [a.key, decision])));
  };
  const setAll = (decision: Decision) => {
    if (!perms) return;
    const editable = perms.groups.filter((g) => !g.locked).flatMap((g) => g.actions);
    save(Object.fromEntries(editable.map((a) => [a.key, decision])));
  };

  const next = async () => {
    setBusy(true);
    try {
      const { data } = await setupApi.safePermissions();
      onView(data);
    } catch (err) {
      toast.error(errorText(err, 'Could not move on. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const activeApp = apps.find((a) => a.slug === active);

  return (
    <SetupFrame step="permissions" wide>
      <StepTitle
        eyebrow="Your permissions"
        title="What may AI do in each app?"
        lead="These are your own rules. If you skip, reading asks you first and anything that changes data stays blocked."
        aside={<div className="max-w-[300px] rounded-[10px] bg-[#eef3ff] p-4 text-[13px] leading-[1.5]"><b className="block text-[#18191d]">You can change these later.</b><span className="text-[#666971]">They live in Settings, under your apps.</span></div>}
      />
      <div className="grid gap-4 md:grid-cols-[220px_1fr]">
        <div className="space-y-2">
          {apps.map((app) => (
            <button
              key={app.slug}
              type="button"
              onClick={() => setActive(app.slug)}
              className={`flex w-full items-center gap-2.5 rounded-[8px] border bg-white px-3 py-2.5 text-left ${app.slug === active ? 'border-[#18191d]' : 'border-[#e6e6e3]'}`}
            >
              <AppMark app={app} />
              <span className="min-w-0">
                <b className="block truncate text-[13px] text-[#18191d]">{app.label}</b>
                <small className="text-[11.5px] text-[#777982]">{touched.has(app.slug) ? 'Set by you' : 'Safe defaults'}</small>
              </span>
            </button>
          ))}
        </div>
        <div className={PANEL}>
          {loading && <div className="flex justify-center p-10"><Loader2 size={18} className="animate-spin text-[#777982]" /></div>}
          {!loading && perms && activeApp && (
            <div>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-[8px] bg-[#f6f6f4] px-3 py-2.5">
                <b className="text-[13.5px] text-[#18191d]">Set every {activeApp.label} action</b>
                <DecisionControl value={null} onChange={setAll} disabled={saving} />
              </div>
              {perms.groups.map((g) => (
                <div key={g.key} className="border-t border-[#ececea] py-3 first:border-t-0">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <b className="text-[13.5px] text-[#18191d]">{g.label}</b>
                      <small className="block text-[12px] text-[#777982]">
                        {g.locked ? 'AI never deletes anything. Always blocked.' : `${g.actions.length} actions`}
                        {!g.locked && (
                          <button type="button" className="ml-2 font-semibold text-[#18191d] underline underline-offset-2" onClick={() => setOpen((o) => ({ ...o, [g.key]: !o[g.key] }))}>
                            {open[g.key] ? 'Hide' : 'See each'}
                          </button>
                        )}
                      </small>
                    </div>
                    <DecisionControl value={g.locked ? 'no' : g.decision} onChange={(d) => setGroup(g.key, d)} disabled={saving || g.locked} />
                  </div>
                  {open[g.key] && !g.locked && (
                    <div className="mt-2 space-y-1.5 rounded-[8px] bg-[#f6f6f4] p-2.5">
                      {g.actions.map((a) => (
                        <div key={a.key} className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-[12.5px] text-[#18191d]">{a.label}</span>
                          <DecisionControl small value={a.decision} onChange={(d) => save({ [a.key]: d })} disabled={saving} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {!loading && !perms && <p className="text-[13.5px] text-[#666971]">Could not load this app's actions. Safe defaults will apply.</p>}
        </div>
      </div>
      <Footer
        left={<button type="button" className={BTN_TEXT} onClick={next} disabled={busy}>Use safe defaults</button>}
        right={<button type="button" className={BTN} onClick={next} disabled={busy || saving}>{busy && <Loader2 size={15} className="animate-spin" />}See ideas</button>}
      />
    </SetupFrame>
  );
};

// ── 04 Ideas ────────────────────────────────────────────────────────────────

const ANALYSIS_STEPS = ['Role and work understood', 'Apps and permissions understood', 'Preparing skills, plugins and builds'];

export const IdeasStep: React.FC<Props> = ({ view, onView }) => {
  const ideas = view.setup?.ideas || [];
  const [finding, setFinding] = useState(ideas.length === 0);
  const [tick, setTick] = useState(0);
  const [picked, setPicked] = useState<Set<string>>(new Set(view.setup?.picked || []));
  const [busy, setBusy] = useState(false);

  const find = useCallback(async () => {
    setFinding(true);
    setTick(0);
    try {
      const { data } = await setupApi.findIdeas();
      onView(data);
      setPicked(new Set());
    } catch (err) {
      toast.error(errorText(err, 'Could not find ideas. Try again.'));
    } finally {
      setFinding(false);
    }
  }, [onView]);

  // Once on arrival, even when React mounts twice: a second search finishing
  // after the person picked would replace their ideas and drop the picks.
  const searched = useRef(false);
  useEffect(() => {
    if (searched.current || ideas.length > 0) return;
    searched.current = true;
    find();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!finding) return;
    const timer = window.setInterval(() => setTick((t) => Math.min(t + 1, 2)), 1400);
    return () => window.clearInterval(timer);
  }, [finding]);

  const toggle = (id: string) => setPicked((p) => {
    const next = new Set(p);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const { opening, cross } = useCross();

  const go = async (ids: string[]) => {
    if (ids.length === 0) {
      setBusy(true);
      goDashboard();
      return;
    }
    if (neededApps(view, ids).length > 0) {
      setBusy(true);
      try {
        await setupApi.pick(ids);
        const { data } = await setupApi.step('connect');
        onView(data);
      } catch (err) {
        toast.error(errorText(err, 'Could not save your picks. Try again.'));
      } finally {
        setBusy(false);
      }
      return;
    }
    cross(async () => {
      await setupApi.pick(ids);
      return (await setupApi.handoffIdeas(ids)).data.path;
    });
  };

  if (finding) {
    const appCount = view.setup?.apps.length || 0;
    return (
      <SetupFrame step="ideas">
        <div className="mx-auto flex max-w-[520px] flex-col items-center text-center">
          <span className="mb-4 flex h-14 w-14 items-center justify-center gap-[7px] rounded-[14px] bg-[#ffea00]"><i className="block h-[9px] w-[5px] animate-pulse rounded-full bg-[#18191d]" /><i className="block h-[9px] w-[5px] animate-pulse rounded-full bg-[#18191d]" /></span>
          <div className="mb-2 font-mono text-[11.5px] font-semibold uppercase tracking-[0.1em] text-[#777982]">Finding ideas</div>
          <h1 className="text-[28px] font-bold leading-tight text-[#18191d]">Matching your role and apps to useful work.</h1>
          <p className="mt-2 text-[14px] leading-[1.6] text-[#666971]">
            I am using your role, your answer about your work, the {appCount} {appCount === 1 ? 'app' : 'apps'} you picked and your permissions. I have not opened any app.
          </p>
          <div className="mt-6 w-full rounded-[10px] border border-[#e6e6e3] bg-white px-4 text-left">
            {ANALYSIS_STEPS.map((label, i) => (
              <div key={label} className="flex items-center gap-3 border-t border-[#ececea] py-3 text-[13.5px] first:border-t-0">
                <span className={`h-2.5 w-2.5 rounded-full ${i < tick ? 'bg-[#1d7a45]' : i === tick ? 'animate-pulse bg-[#ffea00]' : 'bg-[#dedede]'}`} />
                <span className={i <= tick ? 'text-[#18191d]' : 'text-[#92949d]'}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </SetupFrame>
    );
  }

  return (
    <SetupFrame step="ideas">
      <StepTitle
        eyebrow="Ideas from your setup"
        title="Here is what could help. Pick any."
        lead="These come from your role, your apps and your permissions. Nothing has been read from your apps."
      />
      <div className="overflow-hidden rounded-[12px] border border-[#e6e6e3] bg-white">
        {(view.setup?.ideas || []).map((idea) => {
          const on = picked.has(idea.id);
          const [fitLabel, fitTone] = FIT_LABEL[idea.fit];
          return (
            <button
              key={idea.id}
              type="button"
              onClick={() => toggle(idea.id)}
              aria-pressed={on}
              className={`grid w-full grid-cols-[22px_1fr] items-center gap-3 border-t border-[#ececea] px-4 py-3 text-left first:border-t-0 sm:grid-cols-[22px_1fr_auto] ${on ? 'bg-[#eef3ff]' : 'bg-white hover:bg-[#fafaf9]'}`}
            >
              <span className={`h-4 w-4 rounded-[4px] border-[1.5px] ${on ? 'border-[#18191d] bg-[#18191d]' : 'border-[#92949d]'}`} />
              <span className="min-w-0">
                <b className="block text-[14px] text-[#18191d]">{idea.title}</b>
                <small className="block text-[12.5px] leading-[1.45] text-[#666971]">{idea.summary}</small>
                {idea.apps.length > 0 && <span className="mt-1 flex flex-wrap gap-1">{idea.apps.map((a) => <Chip key={a}>{a}</Chip>)}</span>}
              </span>
              <span className="col-start-2 flex flex-wrap items-center gap-1.5 sm:col-start-3 sm:justify-end" title={idea.fit_note}>
                <Chip>{KIND_LABEL[idea.kind]}</Chip>
                <Chip tone={fitTone}>{fitLabel}</Chip>
              </span>
            </button>
          );
        })}
      </div>
      <Footer
        left={
          <span className="flex gap-4">
            <button type="button" className={BTN_TEXT} onClick={() => go([])} disabled={busy || opening}>Skip ideas</button>
            <button type="button" className={BTN_TEXT} onClick={find} disabled={busy || opening}>Look again</button>
          </span>
        }
        right={<button type="button" className={BTN} onClick={() => go(Array.from(picked))} disabled={busy || opening || picked.size === 0}>{(busy || opening) && <Loader2 size={15} className="animate-spin" />}Continue with {picked.size}</button>}
      />
    </SetupFrame>
  );
};

// ── 05 Connect ──────────────────────────────────────────────────────────────

export const ConnectStep: React.FC<Props> = ({ view, onView }) => {
  const rows = useMemo(() => neededApps(view, view.setup?.picked || []), [view]);
  const [status, setStatus] = useState<Record<string, boolean>>({});
  const [later, setLater] = useState<Set<string>>(new Set());
  const { opening, cross } = useCross();

  const refresh = useCallback(async () => {
    const pairs = await Promise.all(rows.map(async ({ app }) => {
      try {
        const { data } = await aaiApi.getConnectorStatus(app.slug, 'personal');
        return [app.slug, !!data.connected] as const;
      } catch {
        return [app.slug, false] as const;
      }
    }));
    setStatus(Object.fromEntries(pairs));
  }, [rows]);

  const { busy: connecting, connect } = usePersonalConnect(refresh);

  useEffect(() => { refresh(); }, [refresh]);

  const next = () => cross(async () => (await setupApi.handoffIdeas()).data.path);

  const back = async () => {
    const { data } = await setupApi.step('ideas');
    onView(data);
  };

  const ideaCount = (view.setup?.picked || []).length;

  return (
    <SetupFrame step="connect">
      <StepTitle
        eyebrow="Connect"
        title={`Your ${ideaCount === 1 ? 'idea needs' : `${ideaCount} ideas need`} these apps.`}
        lead="Connecting lets an idea run for real, with your permissions. You can do it later from Settings; nothing here is required."
      />
      <div className="overflow-hidden rounded-[12px] border border-[#e6e6e3] bg-white">
        {rows.map(({ app, ideas }) => {
          const connected = status[app.slug];
          return (
            <div key={app.slug} className="grid grid-cols-[30px_1fr] items-center gap-3 border-t border-[#ececea] px-4 py-3 first:border-t-0 sm:grid-cols-[30px_1fr_auto]">
              <AppMark app={app} />
              <div className="min-w-0">
                <b className="block text-[14px] text-[#18191d]">{app.label}</b>
                <small className="text-[12.5px] text-[#666971]">Needed by {ideas.join(', ')}</small>
              </div>
              <div className="col-start-2 flex items-center gap-2 sm:col-start-3">
                {connected && <Chip tone="good">Connected</Chip>}
                {!connected && later.has(app.slug) && <Chip>Later</Chip>}
                {!connected && !later.has(app.slug) && (
                  <button type="button" className={BTN_TEXT} onClick={() => setLater((l) => new Set(l).add(app.slug))}>Later</button>
                )}
                {!connected && (
                  <button
                    type="button"
                    className={BTN_OUTLINE}
                    disabled={!!connecting}
                    onClick={() => connect({ slug: app.slug, label: app.label } as unknown as ConnectorRow)}
                  >
                    {connecting === app.slug && <Loader2 size={14} className="animate-spin" />}Connect
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <Footer
        left={<button type="button" className={BTN_TEXT} onClick={back}>Back to ideas</button>}
        right={<button type="button" className={BTN} onClick={next} disabled={opening}>{opening && <Loader2 size={15} className="animate-spin" />}Continue to Workspace</button>}
      />
    </SetupFrame>
  );
};

// ── 06 Start ────────────────────────────────────────────────────────────────

/* Setup no longer ends on its own screen; anyone whose saved step is still
   here lands on the dashboard. */
export const StartStep: React.FC<Props> = () => {
  const moved = useRef(false);
  useEffect(() => {
    if (moved.current) return;
    moved.current = true;
    goDashboard();
  }, []);
  return <div className="flex min-h-screen items-center justify-center bg-[#f6f6f4]"><Loader2 size={22} className="animate-spin text-[#92949d]" /></div>;
};
